import { DestroyRef, Injectable, OnDestroy, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Observable, Subject, fromEvent } from 'rxjs';
import { filter, takeWhile } from 'rxjs/operators';
import { AuthService } from '../security/auth.service';
import { environment } from '../../config/cabinet-environment';

export enum WebSocketConnectionState {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  RECONNECTING = 'reconnecting',
  ERROR = 'error',
  CLOSED = 'closed'
}

export interface WebSocketMessage<T = unknown> {
  type: string;
  data: T;
  timestamp?: number;
  id?: string;
}

@Injectable({
  providedIn: 'root'
})
export class WebSocketService implements OnDestroy {
  private authService = inject(AuthService);

  private socket: WebSocket | null = null;
  private destroyRef = inject(DestroyRef);
  private reconnectAttempts = 0;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private connectionTimer: ReturnType<typeof setTimeout> | null = null;
  private messageQueue: WebSocketMessage[] = [];

  // Connection state management
  private readonly connectionStateSignal = signal<WebSocketConnectionState>(WebSocketConnectionState.DISCONNECTED);
  private readonly connectionState$$ = toObservable(this.connectionStateSignal);
  private messages$ = new Subject<WebSocketMessage>();
  private errors$ = new Subject<Error>();

  // Configuration
  private readonly wsUrl = environment.SOCKET_ADDRESS;
  private readonly maxReconnectAttempts = 10;
  private readonly heartbeatInterval = 30000; // 30 seconds
  private readonly connectionTimeout = 10000; // 10 seconds

  constructor() {
    // WebSocketService is providedIn:'root' — instantiated lazily, often
    // before authService.isAuthenticated flips to true. Try once now, poll
    // briefly otherwise so the connection eventually opens once the token
    // is in memory (without making every consumer call connect() defensively).
    if (this.authService.isAuthenticated) {
      this.connect();
    } else {
      this.scheduleAuthAwareConnect();
    }

    this.setupAuthenticationListener();
    this.setupNetworkListener();
  }

  private scheduleAuthAwareConnect(): void {
    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      const connected = this.connectionStateSignal() === WebSocketConnectionState.CONNECTED;
      if (connected || attempts > 30) {
        clearInterval(interval);
        return;
      }
      if (this.authService.isAuthenticated) {
        clearInterval(interval);
        this.connect();
      }
    }, 1000);
  }

  ngOnDestroy(): void {
    this.disconnect();
  }

  /**
   * Get the current connection state as an observable
   */
  get connectionState(): Observable<WebSocketConnectionState> {
    return this.connectionState$$;
  }

  /**
   * Get incoming messages as an observable
   */
  get messages(): Observable<WebSocketMessage> {
    return this.messages$.asObservable();
  }

  /**
   * Get connection errors as an observable
   */
  get errors(): Observable<Error> {
    return this.errors$.asObservable();
  }

  /**
   * Check if currently connected
   */
  get isConnected(): boolean {
    return this.connectionStateSignal() === WebSocketConnectionState.CONNECTED;
  }

  /**
   * Establish WebSocket connection
   */
  connect(): void {
    if (this.socket?.readyState === WebSocket.CONNECTING ||
      this.socket?.readyState === WebSocket.OPEN) {
      return; // Already connecting or connected
    }

    if (!this.authService.isAuthenticated) {
      return;
    }

    this.updateConnectionState(WebSocketConnectionState.CONNECTING);

    try {
      const token = this.authService.getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }
      const socketUrl = `${this.wsUrl}?token=${encodeURIComponent(token)}&source=cabinet`;

      this.socket = new WebSocket(socketUrl);
      this.setupSocketEventHandlers();
      this.startConnectionTimeout();

    } catch (error) {
      this.handleConnectionError(error);
    }
  }

  /**
   * Disconnect WebSocket
   */
  disconnect(): void {
    this.clearTimers();
    this.reconnectAttempts = 0;

    if (this.socket) {
      this.socket.close(1000, 'Manual disconnect');
      this.socket = null;
    }

    this.updateConnectionState(WebSocketConnectionState.DISCONNECTED);
  }

  /**
   * Send message through WebSocket
   */
  sendMessage<T>(type: string, data: T): void {
    const message: WebSocketMessage<T> = {
      type,
      data,
      timestamp: Date.now(),
      id: this.generateMessageId()
    };

    if (this.isConnected && this.socket) {
      try {
        this.socket.send(JSON.stringify(message));
      } catch (error) {
        this.queueMessage(message);
      }
    } else {
      // Queue message for when connection is restored
      this.queueMessage(message);

      // Attempt to reconnect if not already trying
      if (this.connectionStateSignal() === WebSocketConnectionState.DISCONNECTED) {
        this.reconnect();
      }
    }
  }

  /**
   * Send raw payload directly to WebSocket (for activity tracking compatibility)
   * This bypasses the standard message wrapper for payloads that need specific format
   */
  sendRawMessage(payload: Record<string, unknown>): void {
    if (this.isConnected && this.socket) {
      try {
        this.socket.send(JSON.stringify(payload));
      } catch (error) {
        // Failed to send raw message - silently ignore
      }
    } else {

      // Attempt to reconnect if not already trying
      if (this.connectionStateSignal() === WebSocketConnectionState.DISCONNECTED) {
        this.reconnect();
      }
    }
  }

  /**
   * Subscribe to specific message types
   */
  onMessage<T>(messageType: string): Observable<WebSocketMessage<T>> {
    return this.messages$.pipe(
      filter((message): message is WebSocketMessage<T> => message.type === messageType),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  /**
   * Force reconnection
   */
  reconnect(): void {
    this.disconnect();
    setTimeout(() => this.connect(), 100);
  }

  private setupSocketEventHandlers(): void {
    if (!this.socket) return;

    this.socket.onopen = (event) => {
      this.updateConnectionState(WebSocketConnectionState.CONNECTED);
      this.reconnectAttempts = 0;
      this.startHeartbeat();
      this.processMessageQueue();
      this.clearConnectionTimeout();
    };

    this.socket.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        this.messages$.next(message);
      } catch (error) {
        // Failed to parse incoming message
      }
    };

    this.socket.onclose = (event) => {
      this.clearTimers();

      if (event.code === 1000) {
        // Normal closure
        this.updateConnectionState(WebSocketConnectionState.CLOSED);
      } else {
        // Unexpected closure - attempt reconnection
        this.handleConnectionError(new Error(`Connection closed unexpectedly: ${event.reason}`));
      }
    };

    this.socket.onerror = () => {
      this.handleConnectionError(new Error('WebSocket connection error'));
    };
  }

  private setupAuthenticationListener(): void {
    if (!this.authService.isAuthenticated) {
      return;
    }
    // Reconnect when user authenticates
    // This will now use the shared observable, preventing duplicate requests
    this.authService.refreshToken().pipe(
      takeUntilDestroyed(this.destroyRef),
      takeWhile(() => this.authService.isAuthenticated)
    ).subscribe({
      next: () => {
        if (this.connectionStateSignal() === WebSocketConnectionState.DISCONNECTED) {
          this.connect();
        }
      },
      error: () => {
        this.disconnect();
      }
    });
  }

  private setupNetworkListener(): void {
    // Listen for online/offline events
    fromEvent(window, 'online').pipe(
      takeUntilDestroyed(this.destroyRef),
      takeWhile(() => this.authService.isAuthenticated)
    ).subscribe(() => {
      if (this.connectionStateSignal() !== WebSocketConnectionState.CONNECTED) {
        this.reconnect();
      }
    });

    fromEvent(window, 'offline').pipe(
      takeUntilDestroyed(this.destroyRef),
      takeWhile(() => this.authService.isAuthenticated)
    ).subscribe(() => {
      this.updateConnectionState(WebSocketConnectionState.ERROR);
    });
  }

  private handleConnectionError(error: unknown): void {
    this.errors$.next(error instanceof Error ? error : new Error(String(error)));
    this.clearTimers();

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.updateConnectionState(WebSocketConnectionState.RECONNECTING);
      this.scheduleReconnection();
    } else {
      this.updateConnectionState(WebSocketConnectionState.ERROR);
    }
  }

  private scheduleReconnection(): void {
    this.reconnectAttempts++;
    const delay = this.calculateReconnectDelay();

    setTimeout(() => {
      if (this.authService.isAuthenticated) {
        this.connect();
      }
    }, delay);
  }

  private calculateReconnectDelay(): number {
    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s (max)
    const baseDelay = 1000; // 1 second
    const exponentialDelay = baseDelay * Math.pow(2, this.reconnectAttempts - 1);
    return Math.min(exponentialDelay, 30000); // Cap at 30 seconds
  }

  private startHeartbeat(): void {
    this.heartbeatTimer = setInterval(() => {
      if (this.isConnected) {
        this.sendMessage('ping', { timestamp: Date.now() });
      }
    }, this.heartbeatInterval);
  }

  private startConnectionTimeout(): void {
    this.connectionTimer = setTimeout(() => {
      if (this.socket?.readyState === WebSocket.CONNECTING) {
        this.socket.close();
        this.handleConnectionError(new Error('Connection timeout'));
      }
    }, this.connectionTimeout);
  }

  private clearConnectionTimeout(): void {
    if (this.connectionTimer) {
      clearTimeout(this.connectionTimer);
      this.connectionTimer = null;
    }
  }

  private clearTimers(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    this.clearConnectionTimeout();
  }

  private updateConnectionState(state: WebSocketConnectionState): void {
    if (this.connectionStateSignal() !== state) {
      this.connectionStateSignal.set(state);
    }
  }

  private queueMessage(message: WebSocketMessage): void {
    this.messageQueue.push(message);

    // Limit queue size to prevent memory issues
    if (this.messageQueue.length > 100) {
      this.messageQueue.shift(); // Remove oldest message
    }
  }

  private processMessageQueue(): void {
    if (!this.isConnected || this.messageQueue.length === 0) {
      return;
    }

    while (this.messageQueue.length > 0 && this.isConnected) {
      const message = this.messageQueue.shift();
      if (message && this.socket) {
        try {
          this.socket.send(JSON.stringify(message));
        } catch (error) {
          // Re-queue the message
          this.messageQueue.unshift(message);
          break;
        }
      }
    }
  }

  private generateMessageId(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}
